import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  createCRMSegment,
  getCRMSegmentDetail,
  updateCRMSegment,
  evaluateCRMSegmentRules,
} from '../api';
import { TableChip } from '../../components/PremiumDesign';
import { V2Table } from '@/common/components';
import { EyeIcon } from '../../components/Icons';
import {
  SegmentRuleBuilder,
  isSelectFilterField,
  normalizeSelectFilterValue,
} from './SegmentRuleBuilder';
import { useEnterSubmit } from '@/common/hooks';
import { parseApiError, formatName } from '@/common/utils';
import { Button, SkeletonText, SkeletonBox } from '@/components';

function readBaseline(raw) {
  if (Array.isArray(raw)) return { count: raw.length, results: raw };
  if (raw && Array.isArray(raw.results)) {
    return { count: Number(raw.count) || 0, results: raw.results };
  }
  return { count: 0, results: [] };
}
import sendIcon from '../../assets/Send.png';

function BackIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );
}

function RefreshIcon({ className = 'w-4 h-4' }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

export function CRMSegmentModal({
  onClose,
  companyId,
  segmentId,
  isReadOnly = false,
  onStartPush,
}) {
  const queryClient = useQueryClient();
  const [segmentName, setSegmentName] = useState('');
  const [nameError, setNameError] = useState('');
  const [apiErrors, setApiErrors] = useState({});
  const [segmentDesc, setSegmentDesc] = useState('');
  const [rules, setRules] = useState([
    { id: '1', field: '', operator: '', value: '' },
  ]);
  const [matchType, setMatchType] = useState('and');
  const [previewData, setPreviewData] = useState({
    count: 0,
    sample_contacts: [],
  });
  const [previewOverride, setPreviewOverride] = useState(false);
  const [page, setPage] = useState(1);
  const [samplePage, setSamplePage] = useState(1);
  const hydratedSegmentRef = React.useRef(null);

  const {
    data: existingSegment,
    isLoading: isLoadingSegment,
    isFetching: isFetchingSegment,
    isError: isFetchError,
    error: fetchError,
    refetch: refetchSegment,
  } = useQuery({
    queryKey: ['crm-segment-detail', companyId, segmentId, page],
    queryFn: () =>
      getCRMSegmentDetail({
        companyId,
        segmentId,
        params: { page, page_size: 10 },
      }),
    enabled: !!companyId && !!segmentId,
  });

  useEffect(() => {
    setPage(1);
  }, [segmentId]);

  useEffect(() => {
    const detail = fetchError?.response?.data?.detail;
    if (detail === 'Invalid page.' && page !== 1) setPage(1);
  }, [fetchError, page]);

  useEffect(() => {
    setNameError('');
    return () => {
      hydratedSegmentRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (existingSegment && segmentId) {
      if (String(existingSegment.id) !== String(segmentId)) return;
      if (hydratedSegmentRef.current === segmentId) return;
      hydratedSegmentRef.current = segmentId;
      setPreviewOverride(false);
      setSegmentName(existingSegment.name || '');
      setSegmentDesc(existingSegment.description || '');
      // Initial preview will be fetched automatically via API, we don't set static data here.
      try {
        const parsed =
          typeof existingSegment.filters === 'string'
            ? JSON.parse(existingSegment.filters)
            : existingSegment.filters;
        if (parsed && parsed.conditions) {
          setMatchType(parsed.operator?.toLowerCase() === 'or' ? 'or' : 'and');

          const flatRules = [];
          const extractConditions = (conds) => {
            conds.forEach((c) => {
              if (c.conditions) extractConditions(c.conditions);
              else {
                const selectField = isSelectFilterField(c.field, {
                  type: c.type,
                });
                flatRules.push({
                  ...c,
                  id: Date.now().toString() + Math.random(),
                  ...(selectField
                    ? {
                        type: 'select',
                        operator: '',
                        value: normalizeSelectFilterValue(c.value),
                      }
                    : {}),
                });
              }
            });
          };
          extractConditions(parsed.conditions);

          if (flatRules.length > 0) {
            setRules(flatRules);
          } else {
            setRules([{ id: '1', field: '', operator: '', value: '' }]);
          }
        } else {
          setRules([{ id: '1', field: '', operator: '', value: '' }]);
        }
      } catch {
        // Fallback
        setRules([{ id: '1', field: '', operator: '', value: '' }]);
      }
    } else if (!segmentId) {
      // Reset for creation
      setSegmentName('');
      setSegmentDesc('');
      setRules([{ id: '1', field: '', operator: '', value: '' }]);
      setMatchType('and');
      setPreviewData({ count: 0, sample_contacts: [] });
      setPreviewOverride(false);
      setPage(1);
      hydratedSegmentRef.current = null;
    }
    setApiErrors({});
    setNameError('');
  }, [existingSegment, segmentId]);

  const { mutate: handleSaveSegmentAction, isPending: isSaving } = useMutation({
    mutationFn: (payload) =>
      segmentId
        ? updateCRMSegment({ companyId, segmentId, payload })
        : createCRMSegment({ companyId, payload }),
    onSuccess: () => {
      toast.success(
        segmentId
          ? 'Group updated successfully!'
          : 'Group created successfully!',
      );
      queryClient.invalidateQueries({ queryKey: ['crm-segments', companyId] });
      onClose();
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      toast.error(message || 'Failed to save group.');
    },
  });

  const buildSegmentFilterPayloadFromCurrentFormState = () => {
    const validRules = rules.filter((r) => {
      if (!r.field) return false;
      if (isSelectFilterField(r.field, { type: r.type })) {
        return Array.isArray(r.value) && r.value.length > 0;
      }
      return r.operator && r.value !== '';
    });

    const conditions = validRules.map((r) => {
      if (isSelectFilterField(r.field, { type: r.type })) {
        return {
          field: r.field,
          value: normalizeSelectFilterValue(r.value),
        };
      }
      return {
        field: r.field,
        operator: r.operator,
        value: r.value,
        type: r.type || 'string',
      };
    });

    const filterObj = {
      operator: matchType,
      conditions: conditions,
    };

    return {
      name: segmentName,
      description: segmentDesc,
      filters: filterObj,
      match_mode: matchType === 'and' ? 'ALL' : 'ANY',
    };
  };

  const onSave = () => {
    if (!segmentName.trim()) {
      setNameError('Group name is required.');
      return;
    }
    setNameError('');

    const payload = buildSegmentFilterPayloadFromCurrentFormState();
    payload.matched_contacts_count = previewData.count;
    payload.preview_sample = previewData.sample_contacts;
    payload.last_evaluated = new Date().toISOString();

    handleSaveSegmentAction(payload);
  };

  const { mutate: handleManualPreview, isPending: isManualPreviewing } =
    useMutation({
      mutationFn: (payload) => evaluateCRMSegmentRules({ companyId, payload }),
      onSuccess: (data) => {
        setPreviewOverride(true);
        setPreviewData({
          count: data.count || 0,
          sample_contacts: data.sample_contacts || [],
        });
        toast.success('Preview updated!');
      },
      onError: (err) => {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to evaluate group rules.');
      },
    });

  const isPreviewing = isManualPreviewing;
  const baseline = readBaseline(existingSegment?.baseline_contacts);
  const tableContacts = previewOverride
    ? previewData.sample_contacts
    : baseline.results;
  const tableCount = previewOverride
    ? previewData.count
    : baseline.count || Number(existingSegment?.contact_count) || 0;
  const sampleSize = 10;
  const shownContacts = previewOverride
    ? tableContacts.slice(
        (samplePage - 1) * sampleSize,
        samplePage * sampleSize,
      )
    : tableContacts;

  useEffect(() => {
    setSamplePage(1);
  }, [previewOverride, previewData.sample_contacts]);

  const previewColumns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
        defaultWidth: 180,
        minWidth: 120,
        cell: (contact) => {
          const label =
            formatName(contact.name) || contact.email?.split('@')[0] || '-';
          return (
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate" title={label}>
                {label}
              </span>
              {contact?.is_new === true && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/20 text-primary border border-primary/30 whitespace-nowrap">
                  New
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'email',
        label: 'Email',
        defaultWidth: 200,
        minWidth: 140,
        cell: (contact) => (
          <span className="truncate block" title={contact.email || ''}>
            {contact.email || '-'}
          </span>
        ),
      },
      {
        id: 'phone',
        label: 'Phone',
        defaultWidth: 140,
        minWidth: 110,
        cell: (contact) => contact.phone || '-',
      },
      {
        id: 'tags',
        label: 'Tags',
        defaultWidth: 180,
        minWidth: 120,
        cell: (contact) => {
          const tagsArray = (
            Array.isArray(contact.tags)
              ? contact.tags.map((t) =>
                  typeof t === 'object' ? t : { name: String(t) },
                )
              : typeof contact.tags === 'string'
                ? contact.tags.split(',').map((name) => ({ name: name.trim() }))
                : []
          ).filter((t) => t.name);
          if (!tagsArray.length)
            return <span className="text-text-muted">-</span>;
          return (
            <div className="flex flex-row items-center gap-1.5 min-w-0">
              {tagsArray.slice(0, 2).map((tag, i) => (
                <TableChip
                  key={i}
                  tone="primary"
                  title={tag.name}
                  className="max-w-[80px] truncate"
                  style={
                    tag.color
                      ? {
                          backgroundColor: `${tag.color}15`,
                          borderColor: `${tag.color}40`,
                          color: tag.color,
                        }
                      : undefined
                  }
                >
                  {tag.name}
                </TableChip>
              ))}
              {tagsArray.length > 2 && (
                <TableChip
                  tone="neutral"
                  title={tagsArray
                    .slice(2)
                    .map((t) => t.name)
                    .join(', ')}
                >
                  +{tagsArray.length - 2}
                </TableChip>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  const handleRunPreview = () => {
    const fullPayload = buildSegmentFilterPayloadFromCurrentFormState();
    const payload = {
      segment_id: segmentId ? parseInt(segmentId) : 0,
      filters: fullPayload.filters,
    };
    handleManualPreview(payload);
  };

  const handleStartPush = () => {
    if (!onStartPush || !segmentId) return;
    const contactCount =
      existingSegment?.contact_count ?? previewData.count ?? 0;
    if (Number(contactCount) <= 0) {
      toast.error('No matching contacts found in this group.');
      return;
    }
    onStartPush({
      id: segmentId,
      name: existingSegment?.name || segmentName,
      contact_count: contactCount,
      sample_contacts: tableContacts,
    });
  };

  const formRef = useEnterSubmit({
    onSubmit: onSave,
    isPending: isSaving,
  });

  return (
    <div ref={formRef} className="groups-form w-full flex flex-col gap-4 pb-4">
      <div className="flex flex-col gap-4 rounded-2xl bg-white border border-border-default p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg border border-border-default bg-white text-text-primary flex items-center justify-center hover:bg-layer-7-hover transition-colors focus:outline-none shrink-0"
            aria-label="Back to Groups"
          >
            <BackIcon />
          </button>
          <h2 className="text-xl font-bold text-text-primary tracking-tight">
            {segmentId ? 'Edit Group' : 'Create Group'}
          </h2>
        </div>

        {/* Content Body */}
        {isLoadingSegment ? (
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Left Side: Builder skeleton */}
            <div className="w-full lg:w-[42%] shrink-0 flex flex-col gap-4 rounded-2xl bg-form-background-light p-4">
              <div className="flex flex-col gap-2">
                <SkeletonText width="w-28" height="h-4" />
                <SkeletonBox className="w-full h-11" rounded="rounded-xl" />
              </div>
              <div className="flex flex-col gap-2">
                <SkeletonText width="w-24" height="h-4" />
                <SkeletonBox className="w-full h-20" rounded="rounded-xl" />
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <SkeletonText width="w-24" height="h-5" />
                  <div className="flex gap-2">
                    <SkeletonBox className="w-20 h-10" rounded="rounded-xl" />
                    <SkeletonBox className="w-20 h-10" rounded="rounded-xl" />
                  </div>
                </div>
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="p-4 rounded-xl border border-border-default bg-white flex flex-col gap-3"
                  >
                    <div className="flex justify-between items-center">
                      <SkeletonText width="w-20" height="h-4" />
                      <SkeletonBox className="w-4 h-4" rounded="rounded" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <SkeletonBox
                        className="w-full h-10"
                        rounded="rounded-lg"
                      />
                      <SkeletonBox
                        className="w-full h-10"
                        rounded="rounded-lg"
                      />
                      <SkeletonBox
                        className="w-full h-10"
                        rounded="rounded-lg"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side: Live Preview skeleton */}
            <div className="w-full lg:flex-1 flex flex-col gap-3 rounded-2xl bg-form-background-light p-4">
              <div className="flex items-center justify-between shrink-0">
                <SkeletonText width="w-32" height="h-5" />
                <SkeletonBox className="w-32 h-7" rounded="rounded-lg" />
              </div>
              <div className="flex flex-col">
                <V2Table
                  tight
                  bare
                  storageKey="crm_segment_preview_column_widths"
                  columns={previewColumns}
                  data={[]}
                  loading
                  loadingRowCount={5}
                />
              </div>
            </div>
          </div>
        ) : isFetchError &&
          fetchError?.response?.data?.detail !== 'Invalid page.' ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-4 text-center p-6">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-text-primary font-bold text-lg">
                Failed to load group
              </h3>
              <p className="text-text-muted text-sm max-w-sm">
                {fetchError?.response?.data?.detail ||
                  fetchError?.response?.data?.message ||
                  'An unexpected error occurred while loading group details.'}
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => refetchSegment()}
                className="mt-2 px-6 rounded-lg font-medium"
              >
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <div className="groups-create-layout flex flex-col lg:flex-row gap-4 min-w-0 w-full">
            {/* Left Side: Builder */}
            <div className="w-full lg:w-[42%] min-w-0 flex flex-col gap-4 rounded-2xl bg-form-background-light p-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-text-secondary">
                  Group Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. high value leads"
                  value={segmentName}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    setSegmentName(e.target.value);
                    if (e.target.value.trim()) setNameError('');
                  }}
                  className={`w-full h-11 px-4 rounded-xl bg-white border text-text-primary text-sm placeholder:text-text-muted focus:outline-none transition-colors ${nameError || apiErrors?.name ? 'border-status-error focus:border-status-error' : 'border-border-default focus:border-primary'} ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                />
                {(nameError || apiErrors?.name) && (
                  <p className="text-status-error text-xs font-medium">
                    {nameError || apiErrors?.name}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-text-secondary">
                  Description{' '}
                  <span className="text-text-muted font-normal">
                    (Optional)
                  </span>
                </label>
                <textarea
                  placeholder="What is this group used for?"
                  value={segmentDesc}
                  disabled={isReadOnly}
                  onChange={(e) => setSegmentDesc(e.target.value)}
                  rows={3}
                  className={`w-full p-3 rounded-xl bg-white border border-border-default text-text-primary text-sm placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors resize-none ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                />
              </div>

              {/* Rule Builder Component */}
              <SegmentRuleBuilder
                rules={rules}
                setRules={setRules}
                companyId={companyId}
                matchType={matchType}
                setMatchType={setMatchType}
                isReadOnly={isReadOnly}
              />
            </div>

            {/* Right Side: Live Preview */}
            <div className="groups-preview w-full lg:flex-1 min-w-0 max-w-full flex flex-col gap-3 rounded-2xl bg-form-background-light p-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 shrink-0 min-w-0 w-full">
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-bold text-primary flex items-center gap-2">
                    <EyeIcon className="w-4 h-4" />
                    Live Preview
                  </h3>
                  <button
                    onClick={handleRunPreview}
                    disabled={isPreviewing}
                    className="w-8 h-8 rounded-lg bg-surface-mint hover:bg-layer-7-hover text-primary border border-border-default transition-colors flex items-center justify-center disabled:opacity-50"
                    title="Run Preview"
                  >
                    <RefreshIcon
                      className={`w-4 h-4 ${isPreviewing ? 'animate-spin' : ''}`}
                    />
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                  {!isReadOnly && segmentId && onStartPush && (
                    <button
                      type="button"
                      onClick={handleStartPush}
                      className="h-10 sm:h-9 px-4 sm:px-3 rounded-[10px] bg-white border border-primary flex items-center justify-center gap-2 transition-opacity hover:opacity-80 disabled:opacity-50 w-full sm:w-auto shrink-0"
                      title="Send campaign to this group"
                      aria-label="Send campaign"
                    >
                      <img
                        src={sendIcon}
                        alt=""
                        className="w-4 h-4 object-contain shrink-0"
                      />
                      <span className="text-sm font-semibold text-primary whitespace-nowrap">
                        Send
                      </span>
                    </button>
                  )}
                  <div className="text-xs font-semibold px-3 py-2 rounded-lg bg-white text-primary w-full sm:w-fit text-center sm:text-left whitespace-nowrap shrink-0 flex items-center justify-center sm:justify-start">
                    <span className="font-normal mr-1 text-base">
                      {tableCount}
                    </span>
                    Matching Contacts
                  </div>
                </div>
              </div>

              <div className="flex flex-col">
                <div className="w-full min-w-0 max-w-full overflow-x-auto rounded-[12px] bg-white">
                  {previewOverride && tableCount > tableContacts.length ? (
                    <p className="text-xs text-text-muted px-1 pb-2 shrink-0">
                      Showing {tableContacts.length} sample rows of {tableCount}{' '}
                      matching contacts.
                    </p>
                  ) : null}
                  <V2Table
                    tight
                    storageKey="crm_segment_preview_column_widths"
                    columns={previewColumns}
                    data={shownContacts}
                    rowKey={(contact, index) => contact.id || index}
                    loading={
                      isPreviewing ||
                      (isFetchingSegment && !!segmentId && !previewOverride)
                    }
                    loadingRowCount={4}
                    rowClassName={(contact) =>
                      contact?.is_new ? 'bg-primary/5' : ''
                    }
                    empty={
                      segmentId
                        ? 'No contacts in this group.'
                        : 'Click the refresh icon to see matching contacts based on your rules.'
                    }
                    pagination={
                      previewOverride
                        ? {
                            currentPage: samplePage,
                            totalCount: tableContacts.length,
                            itemsPerPage: sampleSize,
                            onChange: setSamplePage,
                          }
                        : segmentId
                          ? {
                              currentPage: page,
                              totalCount: baseline.count,
                              itemsPerPage: 10,
                              onChange: setPage,
                            }
                          : null
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        {!isReadOnly && (
          <div className="groups-save flex justify-end shrink-0">
            <Button
              variant="primary"
              onClick={onSave}
              disabled={isSaving || isLoadingSegment || isFetchError}
              className="h-[44px] px-6 rounded-xl font-bold"
            >
              {isSaving ? 'Saving...' : 'Save Group'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
